import { ElementRef, inject, signal } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { PaginationConstants } from "../constants/constants";
import { SortOption } from "../models/ui/sort-option.model";
import { PaginationUtilities } from "../utilities/pagination.utilities";

export class BaseListPageClass {

    // Identifies this list when looking up the row height measured from its own rendered rows. Held
    // as the key rather than the element so subclasses aren't handed a DOM handle they have no use
    // for. Declared first so the page size below can read it.
    private listKey: string = PaginationUtilities.getListKey(inject(ElementRef).nativeElement);

    // #region Paging
    public skip = signal(0);
    public take: number = PaginationUtilities.getViewportPageSize(this.listKey);
    public totalCount: number = 0;
    public hasNext: boolean = false;
    public canLoadMore: boolean = true;
    // #endregion Paging

    //Selection
    public isSelectAll: boolean = false;
    //selectionItems is going to be filled with selected items when
    //selectAll is false and de-selected items when selectAll is true
    public selectionItems = signal<number[]>([]);
    public selectedCount = signal<number>(0);

    public isLoading: boolean = true;
    public skeletonItems: any[] = [];

    public orderBy: string = "";

    public sortOptions: SortOption[] = [];

    constructor() { }

    //#region Selection
    public toggleSelectAll(): void {
        this.isSelectAll = !this.isSelectAll;
        this.selectionItems.set([]);
        this.calculateSelected();
    }

    public selectionClicked(data: { id: number, checked: boolean }) {
        const isChecked = data.checked;
        const id = data.id;

        if (this.isSelectAll == true) {
            // selectAll is TRUE so only add DESELECTED items to the array

            if (isChecked == false && this.selectionItems().includes(id) == false) {
                this.selectionItems.update(items => [...items, id]);
            } else {
                this.selectionItems.update(items => items.filter(x => x !== id));
            }
        } else {
            //selectAll is FALSE so only add SELECTED items to the array

            if (isChecked == true && this.selectionItems().includes(id) == false) {
                this.selectionItems.update(items => [...items, id]);
            } else {
                this.selectionItems.update(items => items.filter(x => x !== id));
            }
        }

        this.calculateSelected();
    }

    public calculateSelected(): void {
        if (this.isSelectAll == true) {
            this.selectedCount.set(this.totalCount - this.selectionItems().length);
        } else {
            this.selectedCount.set(this.selectionItems().length);
        }
    }
    //#endregion Selection

    //#region Paging
    public async loadMoreDebounce(): Promise<void> {
        setTimeout(()=> {
          this.canLoadMore = true;
        }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
      }

    public resetPagination(): void {
        this.take = PaginationUtilities.getViewportPageSize(this.listKey);
        this.skip.set(0);
    }
    //#endregion Paging

    //#region Query Param Actions
    protected openAddModalFromQueryParams(activatedRoute: ActivatedRoute, modalId: string, openAction: () => void): void {
        if (activatedRoute.snapshot.queryParamMap.get('action') !== 'add') return;

        const tryOpen = () => {
            const el = document.querySelector(modalId);
            if (el) {
                openAction();
                this.clearActionQueryParam();
                return;
            }
            requestAnimationFrame(tryOpen);
        };
        requestAnimationFrame(tryOpen);
    }

    private clearActionQueryParam(): void {
        const url = new URL(window.location.href);
        if (url.searchParams.has('action')) {
            url.searchParams.delete('action');
            const updatedUrl = `${url.pathname}${url.searchParams.toString() ? '?' + url.searchParams.toString() : ''}${url.hash}`;
            window.history.replaceState(window.history.state, '', updatedUrl);
        }
    }
    //#endregion Query Param Actions
}